import { Injectable } from '@angular/core';
import { Gesture, GestureController, ModalController } from '@ionic/angular';
import { ModalOptions } from '@ionic/core';
import { Capacitor } from '@capacitor/core';
import { iosLeaveAnimation } from '@core/services/modal/animations/ios.leave';
import { SwipeToCloseGestureService } from '@core/services/modal/gestures/swipe-to-close-gesture.service';
import { StatusBar, Style } from '@capacitor/status-bar';

@Injectable({
    providedIn: 'root'
})
export class ModalService {
    private swipeToCloseGesture: Gesture;

    constructor(
        private gestureController: GestureController,
        private modalController: ModalController,
        private swipeToCloseGestureService: SwipeToCloseGestureService
    ) {
    }

    async create(options: ModalOptions): Promise<HTMLIonModalElement> {
        const swipeToClose = options.canDismiss;
        const routerOutlet = document.querySelector('ion-router-outlet');

        // Remove iOS modal style for Android devices
        if (Capacitor.getPlatform() !== 'android') {
            // Prevent doing Ionic gesture, we handle it later
            options = {
                presentingElement: await this.modalController.getTop() ?? routerOutlet,
                ...options,
            };
        }

        options = {
            backdropDismiss: false,
            ...options,
            canDismiss: (data?: any, role?: string) => {
                return new Promise<boolean>(resolve => resolve(!!swipeToClose || role === 'close' || role === 'cancel' || role === 'save'))
              },
        };

        if (Capacitor.isNativePlatform()) {
            StatusBar.setBackgroundColor({
                color: '#000000'
            });
            StatusBar.setStyle({
                style: Style.Dark
            });
        }

        const modal = await this.modalController.create(options);

        if (swipeToClose) {
            const leaveAnimation = iosLeaveAnimation(modal, modal.presentingElement);

            modal.addEventListener('ionModalDidPresent', () => {
                this.swipeToCloseGesture = this.swipeToCloseGestureService.createGesture(
                    this.gestureController,
                    modal,
                    leaveAnimation,
                    () => {
                        leaveAnimation.onFinish(async () => {
                            await this.dismiss(undefined, 'gesture', options.id);
                        });
                    }
                );
                this.swipeToCloseGesture.enable(true);
            });
        }

        return modal;
    }

    dismiss(data?: any, role?: string, id?: string): Promise<boolean> {
        // set style back to normal
        if (Capacitor.isNativePlatform()) {
            StatusBar.setBackgroundColor({
                color: '#FFFFFF'
            });
            StatusBar.setStyle({
                style: Style.Light
            });
        }
        this.swipeToCloseGesture?.enable(false);

        let finalRole = role;

        if (role === undefined || role === '') {
            finalRole = 'close';
        }

        return this.modalController.dismiss(data, finalRole, id);
    }

    getTop(): Promise<HTMLIonModalElement> {
        return this.modalController.getTop();
    }

    async closeAllModals() {
        const modal = await this.modalController.getTop();
        if (modal) {
            await modal.dismiss(undefined, "close");
            this.closeAllModals();
        }
    }

    async closeTopModal(): Promise<void> {
        try {
            const element = await this.modalController.getTop();
            element?.dismiss(undefined, "close");
        } catch (error) {
            // console.log(error);
        }
    }
}
